// Shared media pipeline for the public forms.
//
// This lived inside Feedback.jsx and was the only upload path on the site.
// Surveys had no way to accept a photo at all, which is backwards: a guest
// photographing a cold plate is exactly the evidence a satisfaction survey
// wants. Extracting it lets both forms use one bucket, one set of limits and
// one descriptor shape, so anything that reads attachments reads them the
// same way whichever form produced them.

import { publicSupabase as supabase } from './supabase';

export const MAX_ATTACHMENTS = 5;
export const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
export const MAX_VIDEO_SIZE = 50 * 1024 * 1024;

export const MEDIA_ACCEPT =
  'image/jpeg,image/png,image/webp,image/heic,video/mp4,video/quicktime,video/webm';

// Bucket is named for its first caller. Surveys write here too — renaming it
// would break every attachment URL already stored on anonymous_feedback.
const BUCKET = 'feedback-media';

export const isVideo = (file) => (file?.type || '').startsWith('video/');

/**
 * Picks what fits, and reports the first file that does not.
 * Returns { accepted, rejected } — never throws, so the caller can show the
 * message and keep whatever was already chosen.
 */
export function screenFiles(incoming, alreadyChosen = 0, max = MAX_ATTACHMENTS) {
  const remaining = Math.max(0, max - alreadyChosen);
  const candidates = Array.from(incoming || []).slice(0, remaining);
  const accepted = [];
  let rejected = null;

  for (const file of candidates) {
    const limit = isVideo(file) ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
    if (file.size > limit) {
      rejected = file;
      break;
    }
    accepted.push(file);
  }
  return { accepted, rejected };
}

/**
 * Uploads to the public media bucket and returns descriptors to store as jsonb.
 * onProgress receives "2/5" so the submit button can say what it is doing.
 */
export async function uploadMedia(files, onProgress) {
  const uploaded = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    if (onProgress) onProgress(`${i + 1}/${files.length}`);

    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(fileName, file, { cacheControl: '3600', contentType: file.type });
    if (uploadError) throw uploadError;

    const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(fileName);

    uploaded.push({
      url: urlData.publicUrl,
      type: isVideo(file) ? 'video' : 'image',
      filename: fileName,
      size: file.size,
    });
  }
  return uploaded;
}
